import { useEffect } from 'react';
import { Box, Container, Grid, Paper, Typography } from '@mui/material';
import {
  GearSix,
  PaintBrushHousehold,
  Robot,
  RocketLaunch,
} from '@phosphor-icons/react';

interface WelcomeStepProps {
  onValidationChange: (isValid: boolean) => void;
}

const WelcomeStep = ({ onValidationChange }: WelcomeStepProps) => {
  useEffect(() => {
    onValidationChange(true);
  }, [onValidationChange]);

  const iconColor = '#4038A7';
  const iconBg = '#f5f2ff';

  const cards = [
    {
      icon: GearSix,
      title: 'Easy Setup',
      description:
        'Import your menu and business details in minutes with our guided setup process',
    },
    {
      icon: PaintBrushHousehold,
      title: 'Fully Customizable',
      description:
        "Tailor the AI's responses and personality to match your restaurant's unique brand voice",
    },
    {
      icon: RocketLaunch,
      title: 'Instant Impact',
      description:
        'Start handling customer interactions immediately with zero training required',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 4, md: 2 },
      }}
    >
      <Container sx={{ maxWidth: '1000px' }}>
        <Box
          sx={{
            textAlign: 'center',
            py: { xs: 2, md: 4 },
          }}
        >
          <Box
            sx={{
              backgroundColor: iconBg,
              width: 80,
              height: 80,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 2,
            }}
          >
            <Robot size={40} color={iconColor} weight="duotone" />
          </Box>

          <Typography
            variant="h3"
            component="h3"
            sx={{
              fontSize: '40px',
              fontWeight: 700,
              color: 'custom.textBlue',
            }}
          >
            Conversations that Flow
          </Typography>
          <Typography
            variant="h4"
            component="div"
            sx={{
              mb: 2,
              color: 'primary.main',
              fontSize: '40px',
              fontWeight: 700,
            }}
          >
            Intelligence that Feels Human
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{
              maxWidth: 900,
              fontSize: '16px',
              mx: 'auto',
              color: 'text.secondary',
              letterSpacing: '-0.03em',
            }}
          >
            Transform customer interactions with AI that understands context,
            emotion, and intent across voice, chat, and WhatsApp.
          </Typography>

          <Grid container spacing={4} sx={{ mt: 4 }}>
            {cards.map(({ icon: Icon, title, description }, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 2,
                    textAlign: 'left',
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: iconBg,
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1,
                    }}
                  >
                    <Icon size={20} weight="duotone" color={iconColor} />
                  </Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                      fontWeight: '600',
                      fontSize: '16px',
                      mb: 1,
                    }}
                  >
                    {title}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ fontSize: '14px', fontWeight: '400' }}
                  >
                    {description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default WelcomeStep;
